import React from 'react';
import { Phone, FileAudio } from 'lucide-react';
import { shouldSplitByChannels } from '../../lib/recognition/stereo';
import { formatDuration, formatFileSize, estimateProcessingTime } from '../../utils/audioDuration';
import SpeakerSettings from './SpeakerSettings';

/**
 * Фаза «готово к запуску»: что за файл, как его разберём и во что это
 * обойдётся. Человек видит цену решения до того, как потратит квоту.
 *
 * Верстка по «Бумаге» (docs/redesign/2026-09-04-bumaga/, артборд
 * «Распознавание», состояние UPLOADED): слева карточка файла и строка
 * настроек, справа колонка 292px — обещание, цифры и запуск.
 *
 * 🪤 Из артборда здесь нет намеренно «+ Добавить ещё» и сводки по нескольким
 * файлам — экран принимает ровно один файл, пакетной загрузки у нас нет
 * ([→](docs/backlog/batch-processing.md)).
 *
 * Подпись «Вкладку можно закрыть — доделаем сами» с 15.09.2026 честная: задача
 * регистрируется под завершитель bit-history (`onStarted` → `POST /jobs`), и
 * запись доезжает до Истории без браузера (bit-history/app/jobs.py). При
 * выключенной Истории завершитель задачу не берёт — тогда экран обработки
 * просит вкладку не закрывать (ProcessingState, closeHint).
 */

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3';

const ReadyState = ({
  file, duration, analysis, settings, quotaLeft, starting, historyEnabled = true,
  onChange, onStart, onForceMono, onClear,
}) => {
  const byChannels = shouldSplitByChannels(analysis);
  const minutes = duration ? Math.round(duration / 60) : 0;
  // 🔴 Остаток показываем, только когда он известен. Раньше строка рисовалась
  // всегда и при неприехавшей квоте показывала «Останется 0 мин» — человек
  // читал это как «квота кончилась» и не запускал работу.
  const knowQuota = typeof quotaLeft === 'number';
  // Остаток не уходит в минус: «−153 мин» пугает числом, которого не бывает,
  // а настоящий отказ по квоте приходит с сервера и выглядит иначе.
  const left = Math.max(0, (quotaLeft || 0) - minutes);
  const eta = estimateProcessingTime(duration, settings.diarize && !byChannels);

  // Собираем одной строкой, а не тремя выражениями подряд: React иначе делит
  // подпись на отдельные текстовые узлы, и она читается по кускам.
  const meta = [
    formatFileSize(file.size),
    duration ? formatDuration(duration) : null,
    analysis.channels === 2 ? 'стерео' : analysis.channels === 1 ? 'моно' : null,
  ].filter(Boolean).join(' · ');

  const promises = [
    'Расшифровка с таймкодами',
    // Список — это обещание, и оно обязано следовать за настройками.
    // С выключенной галочкой имён не будет, а обещать их значит соврать ровно
    // там, куда смотрят перед запуском.
    byChannels
      ? 'Реплики, разведённые по каналам'
      : settings.diarize
        ? 'Имена участников вместо «Спикер 1»'
        : 'Сплошной текст без разделения по участникам',
    'Знаки препинания и абзацы',
    // При выключенной Истории записи там не будет; протокол собирается с экрана.
    historyEnabled ? 'Запись в Истории — протокол в один клик' : 'Протокол в один клик',
  ];

  const money = [
    ['Обработка займёт', `~${eta} мин`],
    ['Спишется', `${minutes} мин`],
    ...(knowQuota ? [['Останется', `${left} мин`]] : []),
  ];

  return (
    <div className="flex-1 min-h-0 grid gap-3 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_292px] items-start">
      <div className="flex flex-col gap-3 min-h-0">
        <div className="bg-paper-card rounded-card px-[10px] py-2">
          <div className="grid grid-cols-[36px_1fr_auto] items-center gap-[14px] rounded-control px-3 py-[14px]">
            <span className="w-9 h-9 rounded-[10px] bg-paper-bg flex items-center justify-center text-paper-ink">
              {byChannels ? <Phone size={17} strokeWidth={1.75} /> : <FileAudio size={17} strokeWidth={1.75} />}
            </span>
            <div className="min-w-0">
              <div className="truncate font-mono text-[13px] text-paper-ink">{file.name}</div>
              <div className="mt-[3px] text-[12px] text-paper-ink-3">{meta}</div>
            </div>
            <button type="button" onClick={onClear} aria-label="Убрать файл"
                    className="text-[16px] text-paper-hint hover:text-paper-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
              ×
            </button>
          </div>

          {byChannels && (
            <div className="mx-3 mb-3 rounded-control bg-paper-panel px-4 py-[14px] text-[13px] leading-[1.55]">
              <b className="font-semibold text-paper-ink">Похоже на стерео-звонок — разделим по каналам.</b>{' '}
              <span className="text-paper-ink-2">
                В записи два разных канала: оператор и клиент уже разведены. Обработаем по ним —
                это точнее и примерно на треть быстрее, чем разделять участников по голосу.
              </span>{' '}
              <button type="button" onClick={onForceMono}
                      className="underline underline-offset-[3px] text-paper-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
                Обработать как обычную запись
              </button>
            </div>
          )}
        </div>

        <div className="bg-paper-card rounded-panel px-[22px] py-[18px] flex flex-wrap items-center gap-x-[22px] gap-y-3">
          <span className={LABEL}>Настройки</span>

          {/* У стерео-звонка участников разводят каналы — спрашивать их число незачем. */}
          <SpeakerSettings id="recognition-diarize" settings={settings} onChange={onChange}
                           withCount={!byChannels} />
        </div>
      </div>

      <aside className="flex flex-col gap-3">
        <div className="bg-paper-card rounded-panel p-5 flex flex-col gap-[10px] text-[13px] text-paper-ink-2">
          <div className={`${LABEL} mb-1`}>Что получите</div>
          {promises.map((line) => <div key={line}>{line}</div>)}
        </div>

        <div className="bg-paper-card rounded-panel p-5 flex flex-col gap-[6px] text-[13px] text-paper-ink-2">
          {money.map(([name, value]) => (
            <div key={name} className="flex justify-between">
              <span>{name}</span>
              <b className="font-mono font-medium text-paper-ink">{value}</b>
            </div>
          ))}
        </div>

        <button type="button" onClick={onStart} disabled={starting}
                className="rounded-control bg-paper-accent hover:bg-paper-accent-hover py-[14px] text-[14px] font-semibold text-white transition-colors disabled:bg-paper-bg disabled:text-paper-hint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-accent">
          {starting ? 'Запускаем…' : 'Распознать'}
        </button>
      </aside>
    </div>
  );
};

export default ReadyState;
